import { useState, useEffect } from 'react';
import { Search, Bell, Download, Play, AlertCircle, TrendingDown, TrendingUp, Loader2, Beaker } from 'lucide-react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { AuditRun, Gap, AuditConfig } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { auditEngine, gapAnalysisService } from '../../services';
import { AI_SYSTEMS } from '../../config/ai';
import { generateTestData } from '../../utils/generateTestData';
import { DEFAULT_PROMPTS } from '../../services/promptLibrary';
import { AuditConfigModal, AuditConfigData } from './AuditConfigModal';
import { Tooltip } from '../shared';

/**
 * AI Visibility Dashboard - Kodiac Audit
 * Complete redesign matching UX mockup - with real data
 */

export function Dashboard() {
  const { user } = useAuth();
  const [latestAudit, setLatestAudit] = useState<AuditRun | null>(null);
  const [historicalAudits, setHistoricalAudits] = useState<AuditRun[]>([]);
  const [recentGaps, setRecentGaps] = useState<Gap[]>([]);
  const [totalQueries, setTotalQueries] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [generatingTestData, setGeneratingTestData] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;

    try {
      // Load latest audit
      const auditsQuery = query(
        collection(db, 'auditRuns'),
        where('userId', '==', user.uid),
        orderBy('startedAt', 'desc'),
        limit(1)
      );
      const auditsSnapshot = await getDocs(auditsQuery);

      if (!auditsSnapshot.empty) {
        const audit = auditsSnapshot.docs[0].data() as AuditRun;
        setLatestAudit(audit);

        // Load gaps
        const gapsQuery = query(
          collection(db, 'gaps'),
          where('auditRunId', '==', audit.id),
          where('status', '==', 'open'),
          limit(5)
        );
        const gapsSnapshot = await getDocs(gapsQuery);
        setRecentGaps(gapsSnapshot.docs.map((doc) => doc.data() as Gap));

        // Count total queries (AI responses)
        const responsesQuery = query(
          collection(db, 'aiResponses'),
          where('auditRunId', '==', audit.id)
        );
        const responsesSnapshot = await getDocs(responsesQuery);
        setTotalQueries(responsesSnapshot.size);
      }

      // Load historical audits for trend (last 12)
      const historicalQuery = query(
        collection(db, 'auditRuns'),
        where('userId', '==', user.uid),
        orderBy('startedAt', 'desc'),
        limit(12)
      );
      const historicalSnapshot = await getDocs(historicalQuery);
      setHistoricalAudits(
        historicalSnapshot.docs.map((doc) => doc.data() as AuditRun).reverse()
      );
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRunAudit = async (configData: AuditConfigData) => {
    if (!user) return;

    setRunning(true);
    try {
      const configRef = doc(collection(db, 'auditConfigs'));
      const now = Timestamp.now();
      const config: AuditConfig = {
        id: configRef.id,
        userId: user.uid,
        brandName: configData.brandName,
        websiteUrl: '',
        competitors: [],
        activePrompts: configData.selectedPrompts.map((i) => `prompt-${i}`),
        aiSystems: configData.aiSystems,
        cadence: 'manual',
        createdAt: now,
        updatedAt: now,
      };
      await setDoc(configRef, config);

      const prompts = configData.selectedPrompts.map((index) => {
        const prompt = DEFAULT_PROMPTS[index];
        return {
          id: `prompt-${index}`,
          template: prompt.template,
          variables: prompt.variables,
        };
      });

      const auditRunId = await auditEngine.runAudit(
        user.uid,
        config,
        prompts,
        configData.variables
      );

      const responses = await auditEngine.getAuditResponses(auditRunId);
      await gapAnalysisService.analyzeGaps(auditRunId, responses);

      alert('✅ Audit completed successfully!');
      setShowConfigModal(false);
      await loadDashboardData();
    } catch (error) {
      console.error('Error running audit:', error);
      alert('❌ Audit failed. Check console for details.');
    } finally {
      setRunning(false);
    }
  };

  const handleGenerateTestData = async () => {
    if (!user) return;

    setGeneratingTestData(true);
    try {
      await generateTestData(user.uid);
      alert('✅ Test data generated successfully! Refreshing dashboard...');
      await loadDashboardData();
    } catch (error) {
      console.error('Error generating test data:', error);
      alert('❌ Failed to generate test data. Check console for details.');
    } finally {
      setGeneratingTestData(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-t1-primary" />
      </div>
    );
  }

  // Calculate metrics from real data
  const score = latestAudit?.visibilityScore || 0;
  const previousScore = latestAudit?.previousScore || 0;
  const scoreDelta = previousScore ? score - previousScore : 0;
  const criticalGaps = recentGaps.filter((g) => g.impact === 'high').length;
  const newGaps = recentGaps.filter((g) => {
    const createdAt = g.createdAt?.toDate?.();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return createdAt && createdAt > yesterday;
  }).length;
  const activeSystems = AI_SYSTEMS.filter((s) => s.enabled).length;

  // Queries monitored - from actual data
  const queriesMonitored = totalQueries;
  const hasQueryData = totalQueries > 0;

  // Industry average - would need competitor benchmarking feature
  const industryAvg = null; // Not available yet
  const competitorGap = industryAvg ? score - industryAvg : null;

  const lastScanDate = latestAudit?.startedAt?.toDate?.();
  const lastScan = lastScanDate
    ? `${Math.floor((Date.now() - lastScanDate.getTime()) / 3600000)} hours ago`
    : 'Never';

  // Platform scores (would come from AIResponse data per platform)
  const platforms = AI_SYSTEMS.map((system) => ({
    name: system.name,
    score: system.enabled ? score : 0,
    color: score < 30 ? 'bg-red-500' : score < 50 ? 'bg-orange-500' : 'bg-blue-500',
  }));

  // Trend data from historical audit runs
  const hasTrendData = historicalAudits.length >= 2;
  const trend = hasTrendData
    ? historicalAudits.map((audit) => audit.visibilityScore || 0)
    : [score];

  // Ensure we have at least current score in trend
  if (trend.length === 0) {
    trend.push(score);
  }

  const peakScore = trend.length > 0 ? Math.max(...trend) : score;
  const lowScore = trend.length > 0 ? Math.min(...trend) : score;

  // Calculate 30-day change if we have enough historical data
  const has30DayData = historicalAudits.length >= 5;
  const thirtyDayChange = has30DayData
    ? trend[trend.length - 1] - trend[Math.max(0, trend.length - 5)]
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Audit</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Dashboard</span>
        </div>

        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search queries, brands..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t1-primary"
            />
          </div>

          {/* Growth Badge */}
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">
            Growth
          </span>

          {/* Notifications */}
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Alert Banner */}
      {criticalGaps > 0 && (
        <div className="bg-orange-50 border-l-4 border-orange-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            <p className="text-sm text-orange-900">
              <strong>{newGaps} new critical inaccuracies detected</strong> since last scan — {criticalGaps} total critical issues found.
            </p>
          </div>
          <Link to="/analysis" className="text-sm text-orange-800 hover:text-orange-900 underline">
            View inaccuracies
          </Link>
        </div>
      )}

      {/* Test Data Warning */}
      {latestAudit?.isTestData && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 px-6 py-4">
          <p className="text-sm text-yellow-900">
            🧪 <strong>Test Data:</strong> This dashboard is displaying sample data. Run a real audit to see your actual brand visibility.
          </p>
        </div>
      )}

      {/* Main Content */}
      <div className="px-6 py-8">
        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-t1-primary uppercase tracking-wide mb-1">Kodiac Audit</p>
            <h1 className="text-3xl font-semibold text-gray-900">AI Visibility Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">
              {user?.email?.split('@')[0] || 'Brand'} · Last scanned {lastScan} across {activeSystems} AI systems
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleGenerateTestData}
              disabled={generatingTestData}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Beaker className="w-4 h-4" />
              {generatingTestData ? 'Generating...' : 'Generate Test Data'}
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Download className="w-4 h-4" />
              Export report
            </button>
            <button
              onClick={() => setShowConfigModal(true)}
              disabled={running}
              className="flex items-center gap-2 px-4 py-2 bg-t1-primary rounded-lg text-sm font-semibold text-white hover:bg-t1-dark disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              {running ? 'Running...' : 'Run new scan'}
            </button>
          </div>
        </div>

        {/* 4 Metric Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {/* Overall Score */}
          <div className="bg-white rounded-lg border-t-4 border-orange-500 p-6 shadow-sm">
            <p className="text-xs font-medium text-gray-600 uppercase mb-2">Overall Score</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-semibold text-orange-600">{score}</span>
              <span className="text-lg text-gray-400">/100</span>
            </div>
            {scoreDelta !== 0 && (
              <div className={`flex items-center gap-1 ${scoreDelta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {scoreDelta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                <span className="text-xs font-medium">{Math.abs(scoreDelta)} pts vs last month</span>
              </div>
            )}
          </div>

          {/* Critical Inaccuracies */}
          <div className="bg-white rounded-lg border-t-4 border-red-500 p-6 shadow-sm">
            <p className="text-xs font-medium text-gray-600 uppercase mb-2">Critical Inaccuracies</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-semibold text-red-600">{criticalGaps}</span>
            </div>
            <div className="flex items-center gap-1 text-red-600">
              <TrendingUp className="w-3 h-3" />
              <span className="text-xs font-medium">{newGaps} new since last scan</span>
            </div>
          </div>

          {/* Queries Monitored */}
          <div className="bg-white rounded-lg border-t-4 border-blue-500 p-6 shadow-sm">
            <p className="text-xs font-medium text-gray-600 uppercase mb-2">Queries Monitored</p>
            <div className="flex items-baseline gap-2 mb-1">
              {hasQueryData ? (
                <span className="text-4xl font-semibold text-blue-600">{queriesMonitored}</span>
              ) : (
                <Tooltip content="Run an audit scan to track queries across AI platforms">
                  <span className="text-4xl font-semibold text-gray-400">—</span>
                </Tooltip>
              )}
            </div>
            <p className="text-xs text-gray-500">
              {hasQueryData ? `Across ${activeSystems} AI systems · weekly` : 'No query data yet'}
            </p>
          </div>

          {/* Competitor Gap */}
          <div className="bg-white rounded-lg border-t-4 border-blue-500 p-6 shadow-sm">
            <p className="text-xs font-medium text-gray-600 uppercase mb-2">Competitor Gap</p>
            <div className="flex items-baseline gap-2 mb-1">
              {competitorGap !== null ? (
                <span className={`text-4xl font-semibold ${competitorGap >= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  {competitorGap >= 0 ? '+' : ''}{competitorGap}
                </span>
              ) : (
                <Tooltip content="Configure competitor benchmarking to see your competitive position">
                  <span className="text-4xl font-semibold text-gray-400">—</span>
                </Tooltip>
              )}
            </div>
            <p className="text-xs text-gray-500">
              {industryAvg ? (
                <>Industry avg <span className="font-semibold">{industryAvg}</span> · You <span className="font-semibold">{score}</span></>
              ) : (
                'Competitor data not configured'
              )}
            </p>
          </div>
        </div>

        {/* Bottom Row - 2 Cards */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Score by AI System */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Score by AI system</h2>
                <p className="text-xs text-gray-500">Your visibility score per platform</p>
              </div>
              <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded">
                BELOW AVG
              </span>
            </div>

            <div className="flex items-center gap-8">
              {/* Circular Gauge */}
              <div className="relative w-32 h-32 flex-shrink-0">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="16"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    fill="none"
                    stroke="#F97316"
                    strokeWidth="16"
                    strokeDasharray={`${(score / 100) * 352} 352`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-semibold text-orange-600">{score}</span>
                  <span className="text-xs text-gray-400">/100</span>
                </div>
              </div>

              {/* Platform Scores */}
              <div className="flex-1 space-y-3">
                {platforms.map((platform) => (
                  <div key={platform.name} className="flex items-center gap-3">
                    <span className="text-sm text-gray-700 w-20">{platform.name}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                      <div
                        className={`${platform.color} h-2 rounded-full`}
                        style={{ width: `${(platform.score / 100) * 100}%` }}
                      />
                    </div>
                    <span className={`text-sm font-semibold w-8 text-right ${
                      platform.score < 30 ? 'text-red-600' : 'text-blue-600'
                    }`}>
                      {platform.score}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">Overall score</p>
          </div>

          {/* Score Trend */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Score trend — {hasTrendData ? trend.length : '12'} weeks
                </h2>
                <p className="text-xs text-gray-500">Overall visibility score over time</p>
              </div>
              {hasTrendData && scoreDelta < 0 && (
                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                  DECLINING
                </span>
              )}
              {hasTrendData && scoreDelta > 0 && (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                  IMPROVING
                </span>
              )}
            </div>

            {hasTrendData ? (
              <>
                {/* Bar Chart */}
                <div className="flex items-end justify-between gap-2 h-32 mb-4">
                  {trend.map((value, index) => (
                    <div
                      key={index}
                      className={`flex-1 rounded-t ${
                        index === trend.length - 1 ? 'bg-orange-500' : 'bg-gray-200'
                      }`}
                      style={{ height: `${(value / 50) * 100}%` }}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <span>{trend.length} wks ago</span>
                  <span>Now · {score}</span>
                </div>

                {/* Statistics */}
                <div className="flex items-center justify-around pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-2xl font-semibold text-blue-600">{peakScore}</p>
                    <p className="text-xs text-gray-500">Peak score</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-semibold text-red-600">{lowScore}</p>
                    <p className="text-xs text-gray-500">Low score</p>
                  </div>
                  <div className="text-center">
                    {has30DayData && thirtyDayChange !== null ? (
                      <p className={`text-2xl font-semibold ${thirtyDayChange >= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                        {thirtyDayChange >= 0 ? '+' : ''}{thirtyDayChange}
                      </p>
                    ) : (
                      <Tooltip content="Need at least 5 historical scans to calculate 30-day change">
                        <p className="text-2xl font-semibold text-gray-400">—</p>
                      </Tooltip>
                    )}
                    <p className="text-xs text-gray-500">30-day change</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-center">
                <Tooltip content="Run multiple scans over time to see historical trends">
                  <div className="text-gray-400">
                    <p className="text-base font-medium mb-2">No historical data</p>
                    <p className="text-xs">Run scans regularly to track your score over time</p>
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Section - 2 Cards */}
        <div className="grid grid-cols-2 gap-6">
          {/* Critical Inaccuracies */}
          <div className="bg-white rounded-lg border-t-4 border-red-500 p-6 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Critical inaccuracies</h2>
                <p className="text-xs text-gray-500">Factual errors currently being cited by AI systems</p>
              </div>
              <Link to="/analysis" className="text-sm text-t1-primary hover:text-t1-dark">
                View all →
              </Link>
            </div>

            <div className="space-y-4">
              {recentGaps.filter(g => g.impact === 'high').slice(0, 3).map((gap) => (
                <div key={gap.id} className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      {gap.description}
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">
                      {gap.suggestedFix}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded uppercase">
                        {gap.impact}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded uppercase">
                        {gap.category.replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                  <Link
                    to="/gaps"
                    className="px-3 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Fix
                  </Link>
                </div>
              ))}
              {recentGaps.filter(g => g.impact === 'high').length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No critical issues found</p>
              )}
            </div>
          </div>

          {/* Competitive Benchmark */}
          <div className="bg-white rounded-lg border-t-4 border-blue-500 p-6 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Competitive benchmark</h2>
                <p className="text-xs text-gray-500">Your score vs top competitors in your category</p>
              </div>
              <Link to="/competitive" className="text-sm text-t1-primary hover:text-t1-dark">
                Full report →
              </Link>
            </div>

            {industryAvg ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700 w-32">Competitor A</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div className="bg-green-500 h-3 rounded-full" style={{ width: '72%' }} />
                  </div>
                  <span className="text-sm font-semibold text-green-600 w-8 text-right">72</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700 w-32">Industry avg</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div className="bg-blue-500 h-3 rounded-full" style={{ width: `${industryAvg}%` }} />
                  </div>
                  <span className="text-sm font-semibold text-blue-600 w-8 text-right">{industryAvg}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700 w-32">Competitor B</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div className="bg-blue-500 h-3 rounded-full" style={{ width: '58%' }} />
                  </div>
                  <span className="text-sm font-semibold text-blue-600 w-8 text-right">58</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-center">
                <Tooltip content="Add competitors in Settings to enable competitive benchmarking">
                  <div className="text-gray-400">
                    <p className="text-base font-medium mb-2">No competitor data</p>
                    <p className="text-xs">Configure competitors to see how you stack up</p>
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audit Config Modal */}
      <AuditConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSubmit={handleRunAudit}
      />
    </div>
  );
}
